import { useState } from 'react';
import { useLocation, Link } from 'wouter';
import {
    ArrowRight,
    CheckCircle2,
    Calendar,
    Truck,
    Clock,
    ShieldCheck,
    BarChart3,
    FileSpreadsheet,
    PhoneOff,
    AlertTriangle,
    EyeOff,
    Warehouse,
    PackageCheck,
    History,
    ChevronDown,
    Building2,
    Layers,
    Users,
} from 'lucide-react';
import MarketingNav from '../components/marketing/MarketingNav';
import MarketingFooter from '../components/marketing/MarketingFooter';
import { PRICING_CONFIG } from '../config/pricingConfig';
import { BAYWHIZ_FAQ_ITEMS } from './FaqPage';
import { usePageSEO } from '../utils/seo';

function ScreenshotFrame({
    src,
    alt,
    caption,
    urlBar,
    eager = false,
}: {
    src: string;
    alt: string;
    caption?: string;
    urlBar?: string;
    eager?: boolean;
}) {
    return (
        <figure className="rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 bg-slate-100/90 border-b border-slate-200">
                <div className="flex items-center gap-1.5" aria-hidden="true">
                    <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                    <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                    <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                </div>
                {urlBar && (
                    <span className="text-[11px] font-mono text-slate-500 truncate max-w-[240px] sm:max-w-md">
                        {urlBar}
                    </span>
                )}
                <span className="text-[10px] font-semibold uppercase tracking-wider text-teal-800 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded">
                    Live Product UI
                </span>
            </div>
            <div className="bg-white">
                <img
                    src={src}
                    alt={alt}
                    loading={eager ? 'eager' : 'lazy'}
                    className="w-full h-auto block"
                />
            </div>
            {caption && (
                <figcaption className="px-4 py-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-600 leading-relaxed">
                    {caption}
                </figcaption>
            )}
        </figure>
    );
}

const MANUAL_PROBLEMS = [
    {
        icon: FileSpreadsheet,
        title: 'Shared spreadsheets go stale mid-shift',
        description:
            'When appointments live in a spreadsheet or whiteboard, updates made at the receiving desk are invisible to dock leads and gate staff minutes later.',
    },
    {
        icon: PhoneOff,
        title: 'Endless carrier emails and phone tag',
        description:
            'Schedulers spend hours every week trading emails and phone calls just to verify a PO number, find an open door, and confirm a time window.',
    },
    {
        icon: AlertTriangle,
        title: 'Bunched arrivals and dock door conflicts',
        description:
            'Without structured slot capacity and door assignments, trucks arrive simultaneously—backing up the yard while other windows sit idle.',
    },
    {
        icon: EyeOff,
        title: 'No reliable record of arrivals, dwell, or changes',
        description:
            'When a carrier disputes a detention charge or cancels last-minute, paper logs rarely show who approved the slot, which dock was assigned, or when check-in occurred.',
    },
];

const FOUR_STEPS = [
    {
        step: '01',
        title: 'Configure your docks & operating hours',
        description:
            'Set up your facility timezone, operating windows, slot duration, dock door capabilities, and purchase orders (via manual entry or CSV import).',
        href: '/how-it-works',
    },
    {
        step: '02',
        title: 'Carriers request slots via your booking link',
        description:
            'Share your facility booking link. Carriers validate their PO or load number and request an available time window—as a guest or registered carrier.',
        href: '/product',
    },
    {
        step: '03',
        title: 'Approve bookings & assign dock doors',
        description:
            'Review incoming requests in your approval queue. Approve, propose a revised time, decline with a documented reason, or reassign dock doors as priorities shift.',
        href: '/product',
    },
    {
        step: '04',
        title: 'Track check-in, checkout & dwell time',
        description:
            'Record truck arrival, check-in, dock dispatch, and completion in real time. Every timestamp feeds your dwell analytics and permanent appointment audit history.',
        href: '/solutions',
    },
];

const DESIGNED_FOR_PROFILES = [
    {
        icon: Warehouse,
        title: 'Warehouse & Receiving Managers',
        description:
            'Replace inbox scheduling and shift-change confusion with one live dock board showing today’s arrivals, pending approvals, and door status.',
        bullets: [
            'Live Today, 7-Day, and Month schedule views',
            'One-click approval, time revision, or decline',
            'Facility timezone displayed across every view',
        ],
    },
    {
        icon: Layers,
        title: '3PL & Distribution Facilities',
        description:
            'Coordinate inbound supplier POs and outbound carrier pickups across multiple dock doors without forcing carriers to install software.',
        bullets: [
            'PO validation before carriers can request a slot',
            'Guest carrier booking with signed claim links',
            'Equipment & dock capability matching',
        ],
    },
    {
        icon: Building2,
        title: 'Food, Beverage & Cold Storage Docks',
        description:
            'Keep tight appointment windows on track and document exact check-in and completion timestamps for every temperature-sensitive load.',
        bullets: [
            'Configurable grace period & detention thresholds',
            'Timestamped check-in, dock dispatch & completion',
            'Schedule CSV export and printable PDF dwell reports',
        ],
    },
    {
        icon: Users,
        title: 'Operations & Yard Supervisors',
        description:
            'Give gate staff, receiving clerks, and managers a shared source of truth on mobile tablets or desktop monitors.',
        bullets: [
            'Responsive mobile & tablet schedule views',
            'Full audit trail of approvals, dock changes & cancellations',
            'Receiving reconciliation by PO and pallet count',
        ],
    },
];

export default function LandingPage() {
    const [, setLocation] = useLocation();
    const [openFaqIdx, setOpenFaqIdx] = useState<number | null>(0);

    usePageSEO({
        title: 'BayWhiz — Warehouse Dock Scheduling & Carrier Appointment Software',
        description:
            'Stop running your docks through spreadsheets and phone calls. Give carriers a simple way to request appointments, approve bookings, assign dock doors, and track dwell time from one live schedule.',
        canonicalPath: '/',
    });

    const homepageFaqs = BAYWHIZ_FAQ_ITEMS.slice(0, 6);

    return (
        <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-teal-500/20 overflow-x-hidden">
            {/* 1. MODERN RESPONSIVE NAVIGATION */}
            <MarketingNav variant="light" />

            <main>
                {/* 2. PRODUCT-LED HERO */}
                <section className="pt-16 pb-16 sm:pt-20 sm:pb-24 px-4 sm:px-6 bg-gradient-to-b from-slate-50 via-white to-white border-b border-slate-200/80">
                    <div className="max-w-7xl mx-auto">
                        <div className="max-w-4xl mx-auto text-center">
                            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-semibold tracking-wide mb-6">
                                <span className="w-2 h-2 rounded-full bg-teal-600" aria-hidden="true" />
                                Warehouse Dock Scheduling &amp; Carrier Appointment Platform
                            </div>

                            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.12] mb-6">
                                Stop running your docks through spreadsheets and phone calls.
                            </h1>

                            <p className="text-base sm:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto mb-8">
                                Give carriers a simple way to request appointments. Approve bookings, assign dock doors, and track arrivals, check-ins and dwell time—all from one live schedule.
                            </p>

                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3.5 sm:gap-4 mb-4">
                                <button
                                    type="button"
                                    onClick={() => setLocation('/request-trial')}
                                    className="px-7 py-4 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold text-base shadow-md shadow-teal-600/15 transition-colors inline-flex items-center justify-center gap-2 cursor-pointer"
                                >
                                    Request Free Trial
                                    <ArrowRight size={18} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setLocation('/how-it-works')}
                                    className="px-7 py-4 rounded-xl bg-white hover:bg-slate-50 text-slate-800 font-semibold text-base border border-slate-300 transition-colors inline-flex items-center justify-center gap-2 cursor-pointer"
                                >
                                    See How It Works
                                </button>
                            </div>

                            <p className="text-xs sm:text-sm font-medium text-slate-600 mb-12">
                                30-day full-access trial &middot; No credit card required &middot;{' '}
                                <span className="text-slate-900 font-semibold">{PRICING_CONFIG.startingPriceIntro}</span>
                            </p>
                        </div>

                        {/* Prominent Authentic Product Screenshot */}
                        <div className="max-w-6xl mx-auto">
                            <ScreenshotFrame
                                src="/landing/real-warehouse-schedule.png"
                                alt="BayWhiz live warehouse dock schedule showing assigned dock doors D1 through D4, pending carrier approval requests, and upcoming arrivals"
                                urlBar="baywhiz.com/appointments — Live Warehouse Dock Schedule"
                                caption="Actual BayWhiz Schedule interface: manage pending carrier requests, visual dock door timelines (Today, 7 Days, Month), and upcoming truck arrivals from one screen."
                                eager={true}
                            />
                        </div>
                    </div>
                </section>

                {/* 3. PROBLEMS WITH MANUAL DOCK SCHEDULING */}
                <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white border-b border-slate-200/80">
                    <div className="max-w-7xl mx-auto">
                        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
                            <p className="text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
                                Why Manual Scheduling Breaks Down
                            </p>
                            <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
                                Spreadsheets and shared inboxes weren&rsquo;t built for live dock operations
                            </h2>
                            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                                When every appointment change requires a phone call or manual spreadsheet edit, your receiving team spends more time chasing updates than moving freight.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {MANUAL_PROBLEMS.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <div
                                        key={item.title}
                                        className="rounded-2xl border border-slate-200 bg-slate-50/70 p-6 flex flex-col"
                                    >
                                        <div className="w-11 h-11 rounded-xl bg-white border border-slate-200 text-slate-800 flex items-center justify-center mb-4 shadow-sm">
                                            <Icon size={20} className="text-teal-700" />
                                        </div>
                                        <h3 className="text-base font-bold text-slate-900 mb-2">
                                            {item.title}
                                        </h3>
                                        <p className="text-sm text-slate-600 leading-relaxed">
                                            {item.description}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* 4. FOUR-STEP PRODUCT WALKTHROUGH */}
                <section className="py-16 sm:py-24 px-4 sm:px-6 bg-slate-50 border-b border-slate-200/80">
                    <div className="max-w-7xl mx-auto">
                        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
                            <p className="text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
                                End-to-End Dock Workflow
                            </p>
                            <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
                                How BayWhiz runs your dock schedule in four steps
                            </h2>
                            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                                From PO validation to final truck checkout, every step is structured, timestamped, and visible to your warehouse team.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {FOUR_STEPS.map((item) => (
                                <div
                                    key={item.step}
                                    className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm flex flex-col justify-between"
                                >
                                    <div>
                                        <div className="inline-flex items-center justify-center px-3 py-1 rounded-lg bg-teal-50 border border-teal-200 text-teal-800 font-mono text-xs font-bold mb-4">
                                            Step {item.step}
                                        </div>
                                        <h3 className="text-lg font-bold text-slate-900 mb-2.5">
                                            {item.title}
                                        </h3>
                                        <p className="text-sm text-slate-600 leading-relaxed mb-6">
                                            {item.description}
                                        </p>
                                    </div>
                                    <Link
                                        href={item.href}
                                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700 hover:text-teal-800"
                                    >
                                        Explore workflow <ArrowRight size={14} />
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* 5. CARRIER SELF-SERVICE APPOINTMENT BOOKING */}
                <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white border-b border-slate-200/80">
                    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                        <div className="lg:col-span-5">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-semibold mb-4">
                                <Truck size={14} />
                                Carrier Self-Service Portal
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-4">
                                Carriers book open slots online—only against valid POs
                            </h2>
                            <p className="text-slate-600 text-base leading-relaxed mb-6">
                                Share your facility booking link with carriers and brokers. Drivers or dispatchers validate their Purchase Order or Load Number first, then select from real-time available windows based on your dock operating hours.
                            </p>
                            <ul className="space-y-3 text-sm text-slate-700 mb-8">
                                <li className="flex items-start gap-2.5">
                                    <CheckCircle2 size={18} className="text-teal-600 shrink-0 mt-0.5" />
                                    <span>
                                        <strong>PO &amp; Load validation:</strong> Prevents unverified or duplicate bookings against already-scheduled purchase orders.
                                    </span>
                                </li>
                                <li className="flex items-start gap-2.5">
                                    <CheckCircle2 size={18} className="text-teal-600 shrink-0 mt-0.5" />
                                    <span>
                                        <strong>Guest or registered carrier access:</strong> Carriers can submit a request immediately without creating an account, or claim the booking into a carrier dashboard using a secure signed token.
                                    </span>
                                </li>
                                <li className="flex items-start gap-2.5">
                                    <CheckCircle2 size={18} className="text-teal-600 shrink-0 mt-0.5" />
                                    <span>
                                        <strong>Structured shipment details:</strong> Captures carrier company, MC number, driver name, phone, truck/trailer numbers, and pallet count upfront.
                                    </span>
                                </li>
                            </ul>
                            <Link
                                href="/how-it-works"
                                className="inline-flex items-center gap-2 text-sm font-semibold text-teal-700 hover:text-teal-800"
                            >
                                See the full carrier-to-warehouse flow <ArrowRight size={16} />
                            </Link>
                        </div>

                        <div className="lg:col-span-7">
                            <ScreenshotFrame
                                src="/landing/real-carrier-booking.png"
                                alt="BayWhiz carrier self-service appointment request screen showing validated PO-48920 at Pacific Distribution Center, date and time selector, and carrier details form"
                                urlBar="baywhiz.com/carrier-request — Carrier Appointment Request"
                                caption="Step 2 of the Carrier Booking portal: after validating PO-48920, the dispatcher selects an available date and time slot and enters carrier & equipment details."
                            />
                        </div>
                    </div>
                </section>

                {/* 6. WAREHOUSE APPROVALS AND DOCK SCHEDULING */}
                <section className="py-16 sm:py-24 px-4 sm:px-6 bg-slate-50 border-b border-slate-200/80">
                    <div className="max-w-7xl mx-auto">
                        <div className="max-w-3xl mb-12">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-semibold mb-4">
                                <Calendar size={14} />
                                Warehouse Approvals &amp; Dock Door Control
                            </div>
                            <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
                                Stay in full control of approvals and dock door assignments
                            </h2>
                            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                                Carrier requests never overwrite your schedule unannounced. Your receiving team reviews pending requests, assigns specific dock doors, or reassigns doors in seconds when yard priorities change.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="flex flex-col justify-between bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                                <div className="mb-6">
                                    <h3 className="text-lg font-bold text-slate-900 mb-2">
                                        1. Review &amp; approve pending requests
                                    </h3>
                                    <p className="text-sm text-slate-600 leading-relaxed">
                                        Incoming carrier bookings appear in the <strong>Awaiting Approval</strong> queue at the top of your schedule. Approve onto an open dock, propose a revised time slot, or decline with a reason.
                                    </p>
                                </div>
                                <ScreenshotFrame
                                    src="/landing/real-appointment-approval.png"
                                    alt="BayWhiz Awaiting Approval queue showing a pending carrier request with Approve, Revise, and Decline controls"
                                    urlBar="baywhiz.com/appointments — Awaiting Approval Queue"
                                    caption="Warehouse managers can approve, revise time windows, or decline incoming requests directly above the live dock timeline."
                                />
                            </div>

                            <div className="flex flex-col justify-between bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                                <div className="mb-6">
                                    <h3 className="text-lg font-bold text-slate-900 mb-2">
                                        2. Assign &amp; reassign dock doors with conflict checks
                                    </h3>
                                    <p className="text-sm text-slate-600 leading-relaxed">
                                        Click any appointment to view PO details, assign or reassign a dock door (D1–D4), or mark the truck as arrived. Every dock change is logged with user attribution.
                                    </p>
                                </div>
                                <ScreenshotFrame
                                    src="/landing/real-dock-assignment.png"
                                    alt="BayWhiz appointment detail modal showing approved PO-40765 assigned to Dock D1 with Reassign Dock selector open"
                                    urlBar="baywhiz.com/appointments — Dock Door Assignment & Reassignment"
                                    caption="Inspect shipment details, equipment type, expected pallet count, and reassign dock doors from the appointment detail panel."
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* 7. CHECK-IN, CHECKOUT AND OPERATIONAL HISTORY */}
                <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white border-b border-slate-200/80">
                    <div className="max-w-7xl mx-auto">
                        <div className="max-w-3xl mb-12">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-semibold mb-4">
                                <History size={14} />
                                Live Execution &amp; Audit Trail
                            </div>
                            <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
                                Track every check-in, dock dispatch, checkout, and cancellation
                            </h2>
                            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                                Know exactly which trucks are on-site, which doors are actively unloading, and who approved, reassigned, or cancelled any appointment—even after a dock is released.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="flex flex-col justify-between bg-slate-50 rounded-2xl border border-slate-200 p-6">
                                <div className="mb-6">
                                    <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-teal-700 mb-2">
                                        <PackageCheck size={15} />
                                        On-Site Check-In &amp; Receiving
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-2">
                                        Step-by-step arrival, dock dispatch &amp; completion
                                    </h3>
                                    <p className="text-sm text-slate-600 leading-relaxed">
                                        As trucks arrive at the gate, record check-in, dispatch them to their assigned dock door, reconcile received PO quantities, and complete checkout to release the door.
                                    </p>
                                </div>
                                <ScreenshotFrame
                                    src="/landing/real-checkin-checkout.png"
                                    alt="BayWhiz checked-in appointment view showing Dispatch to Dock D4 button, Undo Check-In, Reassign Dock, and PO receiving reconciliation"
                                    urlBar="baywhiz.com/appointments — Checked-In Load Execution"
                                    caption="Checked-in status view with one-click dock dispatch, PO receiving reconciliation, and carrier equipment verification."
                                />
                            </div>

                            <div className="flex flex-col justify-between bg-slate-50 rounded-2xl border border-slate-200 p-6">
                                <div className="mb-6">
                                    <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-teal-700 mb-2">
                                        <ShieldCheck size={15} />
                                        Preserved Operational History
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-2">
                                        Complete audit history—even when carriers cancel
                                    </h3>
                                    <p className="text-sm text-slate-600 leading-relaxed">
                                        When a carrier cancels an approved appointment, BayWhiz automatically frees the dock door for new bookings while preserving who originally approved the load, which docks were assigned, and why it was cancelled.
                                    </p>
                                </div>
                                <ScreenshotFrame
                                    src="/landing/real-appointment-audit-history.png"
                                    alt="BayWhiz appointment audit history showing preserved approval attribution, dock reassignment D1 to D2, dock release notice, and 4 timestamped activity events"
                                    urlBar="baywhiz.com/appointments — Appointment Audit Trail"
                                    caption="Preserved audit trail: shows original approval attribution, dock reassignment history (D1 → D2), released dock status, and carrier cancellation reason."
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* 8. DWELL TRACKING AND VISIBILITY */}
                <section className="py-16 sm:py-24 px-4 sm:px-6 bg-slate-50 border-b border-slate-200/80">
                    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                        <div className="lg:col-span-5">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-semibold mb-4">
                                <BarChart3 size={14} />
                                Dwell &amp; Turnaround Visibility
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-4">
                                Measure dock dwell time and detention exposure from real timestamps
                            </h2>
                            <p className="text-slate-600 text-base leading-relaxed mb-6">
                                Because BayWhiz records check-in and completion times for every load, your dwell metrics are calculated automatically—helping you spot bottlenecks by dock door or carrier without manual stopwatch spreadsheets.
                            </p>
                            <ul className="space-y-3 text-sm text-slate-700 mb-8">
                                <li className="flex items-start gap-2.5">
                                    <CheckCircle2 size={18} className="text-teal-600 shrink-0 mt-0.5" />
                                    <span>
                                        <strong>Configurable grace period &amp; hourly rate:</strong> Set your facility&rsquo;s detention free-time threshold (e.g., 120 minutes) and hourly detention rate to monitor exposure.
                                    </span>
                                </li>
                                <li className="flex items-start gap-2.5">
                                    <CheckCircle2 size={18} className="text-teal-600 shrink-0 mt-0.5" />
                                    <span>
                                        <strong>7-day, 14-day &amp; 30-day trends:</strong> Review average dwell time, gate-to-dock wait time, detention rate, and per-dock turnaround performance.
                                    </span>
                                </li>
                                <li className="flex items-start gap-2.5">
                                    <CheckCircle2 size={18} className="text-teal-600 shrink-0 mt-0.5" />
                                    <span>
                                        <strong>Printable PDF dwell reports &amp; schedule CSV export:</strong> Export printable PDF dwell and detention summaries or download appointment schedule records as CSV for internal operations reviews or carrier billing discussions.
                                    </span>
                                </li>
                            </ul>
                            <Link
                                href="/product"
                                className="inline-flex items-center gap-2 text-sm font-semibold text-teal-700 hover:text-teal-800"
                            >
                                Explore all product capabilities <ArrowRight size={16} />
                            </Link>
                        </div>

                        <div className="lg:col-span-7">
                            <ScreenshotFrame
                                src="/landing/real-dwell-reporting.png"
                                alt="BayWhiz Dwell & Detention Analysis dashboard showing average dwell time, average wait time, detention rate, daily dwell trend chart, and per-dock performance"
                                urlBar="baywhiz.com/dwell-analytics — Dwell & Detention Analysis"
                                caption="Dwell & Detention Analysis dashboard: tracks average dwell time against your facility grace period, daily trends, and dock-by-dock turnaround."
                            />
                        </div>
                    </div>
                </section>

                {/* 9. WHO BAYWHIZ IS DESIGNED FOR */}
                <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white border-b border-slate-200/80">
                    <div className="max-w-7xl mx-auto">
                        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
                            <p className="text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
                                Built for Real Warehouse Teams
                            </p>
                            <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
                                Who BayWhiz is designed for
                            </h2>
                            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                                Purpose-built for warehouse operators who need reliable dock scheduling, carrier self-service booking, and clear execution tracking—without a six-month enterprise WMS project.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {DESIGNED_FOR_PROFILES.map((profile) => {
                                const Icon = profile.icon;
                                return (
                                    <div
                                        key={profile.title}
                                        className="rounded-2xl border border-slate-200 bg-slate-50/60 p-6 sm:p-8 flex flex-col justify-between"
                                    >
                                        <div>
                                            <div className="w-11 h-11 rounded-xl bg-teal-50 border border-teal-200 text-teal-700 flex items-center justify-center mb-4">
                                                <Icon size={22} />
                                            </div>
                                            <h3 className="text-xl font-bold text-slate-900 mb-2.5">
                                                {profile.title}
                                            </h3>
                                            <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-5">
                                                {profile.description}
                                            </p>
                                        </div>
                                        <ul className="space-y-2 border-t border-slate-200/80 pt-4">
                                            {profile.bullets.map((bullet) => (
                                                <li key={bullet} className="flex items-center gap-2 text-xs sm:text-sm text-slate-700 font-medium">
                                                    <CheckCircle2 size={15} className="text-teal-600 shrink-0" />
                                                    <span>{bullet}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* 10. TRANSPARENT PRICING INTRODUCTION */}
                <section className="py-16 sm:py-24 px-4 sm:px-6 bg-slate-50 border-b border-slate-200/80">
                    <div className="max-w-4xl mx-auto">
                        <div className="rounded-2xl bg-white border border-slate-200 p-8 sm:p-12 shadow-sm text-center">
                            <p className="text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
                                Transparent Per-Facility Pricing
                            </p>
                            <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
                                Start with a 30-day full-access trial. {PRICING_CONFIG.startingPriceIntro}
                            </h2>
                            <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto mb-8">
                                Evaluate BayWhiz on your live warehouse dock schedule for {PRICING_CONFIG.trialDays} days with no credit card required at signup. Subscribe in USD per facility only when you are ready to continue—and freight carriers always book for free.
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto mb-8 text-left">
                                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                                    <div className="text-xs font-bold uppercase tracking-wider text-teal-700 mb-1">
                                        30-Day Free Trial
                                    </div>
                                    <div className="text-lg font-extrabold text-slate-900">Full Access</div>
                                    <p className="text-xs text-slate-600 mt-1">
                                        No credit card required at signup. No automatic charges at trial end.
                                    </p>
                                </div>
                                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                                    <div className="text-xs font-bold uppercase tracking-wider text-teal-700 mb-1">
                                        Transparent Pricing
                                    </div>
                                    <div className="text-lg font-extrabold text-slate-900">
                                        ${PRICING_CONFIG.plans.starter.priceMonthlyUsd} &ndash; ${PRICING_CONFIG.plans.operations.priceMonthlyUsd} USD/mo
                                    </div>
                                    <p className="text-xs text-slate-600 mt-1">
                                        Starter at ${PRICING_CONFIG.plans.starter.priceMonthlyUsd} USD/mo and Operations at ${PRICING_CONFIG.plans.operations.priceMonthlyUsd} USD/mo per facility.
                                    </p>
                                </div>
                                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                                    <div className="text-xs font-bold uppercase tracking-wider text-teal-700 mb-1">
                                        Carrier Portal
                                    </div>
                                    <div className="text-lg font-extrabold text-slate-900">Always $0</div>
                                    <p className="text-xs text-slate-600 mt-1">
                                        Carriers never pay to validate POs or request dock appointments.
                                    </p>
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3.5 sm:gap-4">
                                <button
                                    type="button"
                                    onClick={() => setLocation('/request-trial')}
                                    className="px-7 py-4 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold text-base shadow-sm transition-colors inline-flex items-center justify-center gap-2 cursor-pointer"
                                >
                                    Request Free Trial
                                    <ArrowRight size={18} />
                                </button>
                                <Link
                                    href="/pricing"
                                    className="px-7 py-4 rounded-xl bg-white hover:bg-slate-50 text-slate-800 font-semibold text-base border border-slate-300 transition-colors inline-flex items-center justify-center gap-2"
                                >
                                    View Pricing &amp; Plan Details
                                    <ArrowRight size={16} />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 11. FREQUENTLY ASKED QUESTIONS */}
                <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white border-b border-slate-200/80">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <p className="text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
                                Common Questions
                            </p>
                            <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
                                Frequently asked questions
                            </h2>
                            <p className="text-base text-slate-600">
                                Straightforward answers about how the 30-day trial, carrier booking portal, and dock scheduling work.
                            </p>
                        </div>

                        <div className="space-y-3 mb-10">
                            {homepageFaqs.map((item, idx) => {
                                const isOpen = openFaqIdx === idx;
                                const panelId = `home-faq-panel-${idx}`;
                                const btnId = `home-faq-btn-${idx}`;
                                return (
                                    <div
                                        key={item.question}
                                        className="rounded-xl border border-slate-200 bg-slate-50/60 overflow-hidden"
                                    >
                                        <button
                                            id={btnId}
                                            type="button"
                                            aria-expanded={isOpen}
                                            aria-controls={panelId}
                                            onClick={() => setOpenFaqIdx(isOpen ? null : idx)}
                                            className="w-full px-6 py-4 text-left flex items-center justify-between gap-4 font-semibold text-slate-900 hover:bg-slate-100/70 transition-colors cursor-pointer"
                                        >
                                            <span className="text-sm sm:text-base">{item.question}</span>
                                            <ChevronDown
                                                size={18}
                                                className={`text-slate-500 shrink-0 transition-transform duration-200 ${
                                                    isOpen ? 'rotate-180 text-teal-700' : ''
                                                }`}
                                            />
                                        </button>
                                        {isOpen && (
                                            <div
                                                id={panelId}
                                                role="region"
                                                aria-labelledby={btnId}
                                                className="px-6 pb-5 pt-1 text-sm text-slate-600 leading-relaxed border-t border-slate-200/60 bg-white"
                                            >
                                                {item.answer}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="text-center">
                            <Link
                                href="/faq"
                                className="inline-flex items-center gap-2 text-sm font-semibold text-teal-700 hover:text-teal-800"
                            >
                                Read all frequently asked questions <ArrowRight size={16} />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* 12. FINAL 30-DAY TRIAL CTA */}
                <section className="py-16 sm:py-24 px-4 sm:px-6 bg-slate-900 text-white">
                    <div className="max-w-4xl mx-auto text-center">
                        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-teal-950 border border-teal-800 text-teal-300 text-xs font-semibold mb-6">
                            <Clock size={14} />
                            Set Up Your Facility Schedule Today
                        </div>
                        <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white mb-6 leading-tight">
                            Ready to replace dock spreadsheets with one live schedule?
                        </h2>
                        <p className="text-base sm:text-xl text-slate-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                            Create your warehouse account, configure your dock doors, and test carrier appointment requests with your own team for 30 days.
                        </p>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 mb-5">
                            <button
                                type="button"
                                onClick={() => setLocation('/request-trial')}
                                className="px-8 py-4 rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-base shadow-lg transition-colors inline-flex items-center justify-center gap-2 cursor-pointer"
                            >
                                Request Trial Access
                                <ArrowRight size={18} />
                            </button>
                            <button
                                type="button"
                                onClick={() => setLocation('/contact')}
                                className="px-8 py-4 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold text-base border border-slate-700 transition-colors inline-flex items-center justify-center gap-2 cursor-pointer"
                            >
                                Contact Support
                            </button>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-400">
                            30-day full-access trial &middot; No credit card required &middot; Questions?{' '}
                            <a href="mailto:support@baywhiz.com" className="text-teal-300 hover:underline">
                                support@baywhiz.com
                            </a>
                        </p>
                    </div>
                </section>
            </main>

            {/* 13. COMPLETE PROFESSIONAL FOOTER */}
            <MarketingFooter variant="light" />
        </div>
    );
}
